import React, { useRef } from "react";
import { Box, Typography, IconButton, Paper, styled } from "@mui/material";
import { tokens } from "@/tokens";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ScenarioCard } from "@/services/dashboard/sm-api";

const StyledCard = styled(Paper)(() => ({
  padding: "14px 16px",
  borderRadius: tokens.radius.lg,
  cursor: "pointer",
  transition: "all 0.15s ease",
  border: `1px solid ${tokens.border.default}`,
  minWidth: "260px",
  maxWidth: "260px",
  "&:hover": {
    backgroundColor: tokens.bg.hover,
    transform: "translateY(-1px)",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.06)",
  },
}));

const ScrollButton = styled(IconButton)(() => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: tokens.bg.surface,
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
  "&:hover": {
    backgroundColor: tokens.bg.hover,
  },
  zIndex: 1,
}));

interface ScenarioListProps {
  scenarios: ScenarioCard[];
  selectedScenarioId: string | null;
  onScenarioSelect: (scenarioId: string) => void;
}

const ScenarioList: React.FC<ScenarioListProps> = ({
  scenarios,
  selectedScenarioId,
  onScenarioSelect,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 276; // card width + gap
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Reverse the scenarios array to show newer ones on the left
  const sortedScenarios = [...scenarios].reverse();

  return (
    <Box sx={{ position: "relative" }}>
      <ScrollButton onClick={() => scroll("left")} sx={{ left: -20, display: { xs: "none", md: "inline-flex" } }}>
        <ChevronLeftIcon />
      </ScrollButton>

      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          px: 1,
          py: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          scrollBehavior: "smooth",
        }}
      >
        {sortedScenarios.map((scenario) => (
          <StyledCard
            key={scenario._id}
            onClick={() => onScenarioSelect(scenario._id)}
            sx={{
              border: selectedScenarioId === scenario._id ? `2px solid ${tokens.brand.primary}` : undefined,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", mb: 0.75 }}>
                  <Typography sx={{ fontSize: "1rem", lineHeight: 1 }}>
                    {scenario.country_list.length > 1 ? "🌎" : "📈"}
                  </Typography>
                  <Typography
                    sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, fontSize: tokens.fontSize.lg, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {scenario.name}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: tokens.text.excerpt,
                    fontSize: tokens.fontSize.sm2,
                    lineHeight: 1.4,
                  }}
                >
                  Rate: {scenario.rate}% · {scenario.country_list.join(", ")}
                </Typography>
              </Box>
              <ArrowForwardIcon sx={{ color: tokens.brand.primary, fontSize: 18, ml: 1, mt: 0.25, flexShrink: 0 }} />
            </Box>
          </StyledCard>
        ))}
      </Box>

      <ScrollButton onClick={() => scroll("right")} sx={{ right: -20, display: { xs: "none", md: "inline-flex" } }}>
        <ChevronRightIcon />
      </ScrollButton>
    </Box>
  );
};

export default ScenarioList; 